import { IconButton, Stack } from '@mui/material';
import { AvailableRelationGroupFilters } from '@schooly/api';
import { SubjectTagSelect } from '@schooly/components/filters';
import { CrossIcon, TagSelect } from '@schooly/style';
import React, { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';

type InlineFiltersTagsProps = {
  onOpenFilters: () => void;
  onResetFilters?: () => void;
  maxAmount?: number;
  filters: AvailableRelationGroupFilters;
};

export const InlineFiltersTags: FC<InlineFiltersTagsProps> = ({
  filters,
  onOpenFilters,
  onResetFilters,
  maxAmount = 2,
}) => {
  const { schoolId } = useSchool();
  const { formatMessage } = useIntl();
  const tags = useMemo(() => {
    const tagComponents: ReactNode[] = [];

    if (!schoolId) return tagComponents;

    const commonTagsProps = {
      size: 'small',
      onClick: onOpenFilters,
      sx: { maxWidth: 115 },
    } as const;

    for (const id of filters.subject ?? []) {
      tagComponents.push(
        <SubjectTagSelect schoolId={schoolId} key={id} id={id} {...commonTagsProps} />,
      );
    }

    if (filters.only_tutor_groups?.length) {
      tagComponents.push(
        <TagSelect
          key="tutor"
          label={formatMessage({ id: 'groups-OnlyTutorGroups' })}
          {...commonTagsProps}
        />,
      );
    }

    if (tagComponents.length > maxAmount) {
      return [
        ...tagComponents.slice(0, maxAmount),
        <TagSelect
          key="more"
          label={`+${tagComponents.length - maxAmount}`}
          {...commonTagsProps}
        />,
      ];
    }

    return tagComponents;
  }, [filters, schoolId, onOpenFilters, maxAmount, formatMessage]);

  if (!tags.length) return null;

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap={1}>
      {tags}{' '}
      {onResetFilters && (
        <IconButton
          onClick={onResetFilters}
          sx={(theme) => ({
            color: theme.palette.common.grey,
            '&:hover': { color: theme.palette.text.primary },
          })}
        >
          <CrossIcon />
        </IconButton>
      )}
    </Stack>
  );
};
